import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Shuffle } from 'lucide-react';
import { GradientTitle } from '../../components/ui';
import { BiasTest } from '../../components/bias/BiasTest';
import { CategoryFilter } from '../../components/practice/CategoryFilter';
import { ScoreTableau } from '../../components/ui/ScoreTableau';
import type { CognitiveBias } from '../../types';
import { Helmet } from 'react-helmet-async';

interface Question {
  question: string;
  options: string[];
  correctOption: { $numberInt: string };
  explanation: string;
}

interface QuestionWithBias extends Question {
  biasName: string;
  biasId: string;
  shuffledOptions: string[];
}

interface BiasTestQuestion {
  question: string;
  options: string[];
  correctOption: number;
  explanation: string;
}

interface TestPageProps {
  biases: CognitiveBias[];
}

const shuffleArray = <T,>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

export const TestPage: React.FC<TestPageProps> = ({ biases }) => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [allQuestions, setAllQuestions] = useState<QuestionWithBias[]>([]);
  const [shuffleCount, setShuffleCount] = useState(0);
  
  // Score tracking state
  const [totalAttempts, setTotalAttempts] = useState(0);
  const [correctMatches, setCorrectMatches] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<string>>(new Set());
  const [streak, setStreak] = useState(0);

  // When filter changes, get all questions for that category
  useEffect(() => {
    const questions = biases
      .filter(bias => bias.test?.questions.length && 
        (activeFilter === 'All' || bias.category === activeFilter))
      .flatMap(bias => 
        bias.test!.questions.map(q => ({
          question: q.question,
          options: q.options,
          correctOption: { $numberInt: q.correctOption.toString() },
          explanation: q.explanation,
          biasName: bias.name,
          biasId: bias.id,
          shuffledOptions: shuffleArray([...q.options])
        }))
      );
    
    setAllQuestions(questions);
    setCurrentQuestionIndex(0);
  }, [activeFilter, biases]);

  // Get unique categories
  const categories = Array.from(new Set(
    biases.filter(b => b.test?.questions.length).map(b => b.category)
  ));

  const handleShuffle = () => {
    const card = document.querySelector('.test-card');
    card?.classList.add('shuffle-animation');
    
    setTimeout(() => {
      // Shuffle the questions array but preserve each question's options order
      setAllQuestions(shuffleArray([...allQuestions]));
      setCurrentQuestionIndex(0);
      setShuffleCount(count => count + 1);
      card?.classList.remove('shuffle-animation');
    }, 500);
  };

  const handlePrevious = () => {
    setCurrentQuestionIndex(current => 
      current === 0 ? allQuestions.length - 1 : current - 1
    );
  };

  const handleNext = () => {
    setCurrentQuestionIndex(current => 
      current === allQuestions.length - 1 ? 0 : current + 1
    );
  };

  const currentQuestion = allQuestions[currentQuestionIndex];

  const handleAnswerSubmit = (isCorrect: boolean) => {
    const questionKey = currentQuestion.question;
    if (!answeredQuestions.has(questionKey)) {
      setTotalAttempts(prev => prev + 1);
      if (isCorrect) {
        setCorrectMatches(prev => prev + 1);
        setStreak(prev => prev + 1);
      } else {
        setStreak(0);
      }
      setAnsweredQuestions(prev => new Set([...prev, questionKey]));
    }
  };

  // Use pre-shuffled options
  const mapToTestQuestion = (q: QuestionWithBias & { shuffledOptions: string[] }): BiasTestQuestion => {
    const correctAnswerText = q.options[Number(q.correctOption.$numberInt)];
    const newCorrectIndex = q.shuffledOptions.findIndex(opt => opt === correctAnswerText);
    
    return {
      question: q.question,
      options: q.shuffledOptions,
      correctOption: newCorrectIndex,
      explanation: q.explanation
    };
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <Helmet prioritizeSeoTags>
        <title>Cognitive Bias Test | Challenge Your Knowledge</title>
        <meta name="description" content="Challenge yourself with our cognitive bias test. Identify biases in real-world scenarios and enhance your critical thinking skills through interactive quizzes." />
        <meta name="keywords" content="cognitive bias test, bias quiz, critical thinking test, psychology quiz, decision-making assessment, bias identification" />
        <link rel="canonical" href="https://www.cognitivebiaslab.com/practice/test/" key="canonical" data-source="page-component" />
        
        {/* OpenGraph tags for better social sharing */}
        <meta property="og:title" content="Cognitive Bias Test | Challenge Your Knowledge" />
        <meta property="og:description" content="Challenge yourself with our cognitive bias test. Identify biases in real-world scenarios and enhance your critical thinking skills through interactive quizzes." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cognitivebiaslab.com/practice/test/" />
        <meta property="og:site_name" content="Cognitive Bias Lab" />
        <meta property="og:image" content="https://www.cognitivebiaslab.com/logo.webp" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:type" content="image/webp" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Cognitive Bias Test | Challenge Your Knowledge" />
        <meta name="twitter:description" content="Challenge yourself with our cognitive bias test. Identify biases in real-world scenarios and enhance your critical thinking skills through interactive quizzes." />
        <meta name="twitter:image" content="https://www.cognitivebiaslab.com/logo.webp" />
      </Helmet>

      <GradientTitle className="text-3xl md:text-4xl text-center mb-12" useH1={true}>
        Test Your Understanding
      </GradientTitle>

      {/* Score Tableau */}
      <div className="max-w-4xl mx-auto mb-12">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-lg border border-gray-100 mb-12">
          <ScoreTableau
            totalAttempts={totalAttempts}
            correctMatches={correctMatches}
            accuracy={totalAttempts > 0 ? Math.round((correctMatches / totalAttempts) * 100) : 0}
            streak={streak}
          />
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <CategoryFilter
            categories={categories}
            activeFilter={activeFilter}
            onFilterChange={(category) => {
              setActiveFilter(category);
              setCurrentQuestionIndex(0);
            }}
          />

          <button
            onClick={() => {
              handleShuffle();
            }}
            className="group bg-gradient-to-r from-orange-500 to-pink-500 px-6
              rounded-xl text-white shadow-lg hover:shadow-xl transition-all duration-300 
              hover:scale-105 flex items-center gap-2 h-[52px]"
          >
            <Shuffle className="w-5 h-5 group-hover:rotate-180 transition-transform duration-300" />
            Shuffle
          </button>
        </div>
      </div>

      {allQuestions.length > 0 ? (
        <div className="max-w-4xl mx-auto">
          <div className="test-card bg-white/90 backdrop-blur-sm rounded-3xl shadow-lg p-8 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                {currentQuestion.biasName}
              </h2>
              <div className="text-sm text-gray-500">
                Question {currentQuestionIndex + 1} of {allQuestions.length}
              </div>
            </div>

            <BiasTest 
              key={`question-${currentQuestionIndex}-${shuffleCount}-${activeFilter}`}
              test={{
                questions: [mapToTestQuestion(currentQuestion)]
              }}
              variant="transparent"
              onAnswerSubmit={handleAnswerSubmit}
            />

            <div className="flex justify-between mt-8">
              <button
                onClick={handlePrevious}
                className="p-2 rounded-xl hover:bg-orange-50 transition-colors"
              >
                <ChevronLeft className="w-6 h-6 text-gray-600" />
              </button>
              <button
                onClick={handleNext}
                className="p-2 rounded-xl hover:bg-orange-50 transition-colors"
              >
                <ChevronRight className="w-6 h-6 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center text-gray-600">
          No tests available for this category.
        </div>
      )}

      <style>
        {`
          @keyframes shuffle {
            0% { transform: translateX(0) rotate(0); }
            25% { transform: translateX(5px) rotate(5deg); }
            75% { transform: translateX(-5px) rotate(-5deg); }
            100% { transform: translateX(0) rotate(0); }
          }
          .shuffle-animation {
            animation: shuffle 0.5s ease-in-out;
          }
        `}
      </style>
    </div>
  );
};